/**
 * Turning raw control-plane rows into something an operator can read.
 *
 * Every screen in this console renders rows straight out of `control.*`, so
 * the values arriving here are whatever Postgres and asyncpg produced: ISO
 * timestamps, 64-character digests, image references long enough to blow out
 * any column, and nested JSONB evidence objects. The helpers below are the one
 * place that decides how each of those reads, so a digest looks the same on
 * the Deployments screen as it does on the Archive screen.
 */

export type Primitive = string | number | boolean | null | undefined;

const DIGEST = /^(sha256:)?[0-9a-f]{64}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const IMAGE_REFERENCE = /^[a-z0-9.-]+\/\S+@sha256:[0-9a-f]{64}$/;

/** A label for a snake_case column or area id, e.g. `manifest_sha256`. */
export function humanize(key: string): string {
  const spaced = key.replace(/[_-]+/g, ' ').trim();
  if (!spaced) return key;
  const initialism = /^(id|url|uri|api|cpu|ip|sha|ssh|wal|xfs|otel|dns|tls|vpc)$/i;
  return spaced
    .split(' ')
    .map((word, index) => {
      if (initialism.test(word)) return word.toUpperCase();
      if (index === 0) return word.charAt(0).toUpperCase() + word.slice(1);
      return word;
    })
    .join(' ');
}

/** Absolute time, in the operator's locale, or the raw value if unparseable. */
export function formatTimestamp(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * "4 minutes ago". Incident triage is almost always a question about recency,
 * and a wall-clock timestamp makes the reader do the subtraction themselves.
 */
export function formatRelative(value: string, now: number = Date.now()): string {
  const parsed = new Date(value).getTime();
  if (Number.isNaN(parsed)) return '';
  const seconds = Math.round((parsed - now) / 1000);
  const magnitude = Math.abs(seconds);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['second', 60],
    ['minute', 60 * 60],
    ['hour', 60 * 60 * 24],
    ['day', 60 * 60 * 24 * 30],
    ['month', 60 * 60 * 24 * 365],
  ];
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  let divisor = 1;
  for (const [unit, limit] of units) {
    if (magnitude < limit) return formatter.format(Math.trunc(seconds / divisor), unit);
    divisor = limit;
  }
  return formatter.format(Math.trunc(seconds / divisor), 'year');
}

export function isTimestampKey(key: string): boolean {
  return /(_at|_time|timestamp)$/.test(key);
}

/** Digests and image references, shortened but still recognisable. */
export function abbreviate(value: string): string {
  if (IMAGE_REFERENCE.test(value)) {
    const [repository, digest] = value.split('@sha256:');
    const name = repository?.split('/').pop() ?? repository ?? '';
    return `${name}@${digest?.slice(0, 12) ?? ''}`;
  }
  if (DIGEST.test(value)) {
    const digest = value.replace(/^sha256:/, '');
    return `${digest.slice(0, 12)}…`;
  }
  if (UUID.test(value)) return `${value.slice(0, 8)}…`;
  return value;
}

export function isAbbreviated(value: string): boolean {
  return DIGEST.test(value) || UUID.test(value) || IMAGE_REFERENCE.test(value);
}

export function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value as object).length === 0;
  return false;
}

function formatBytes(value: number): string {
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  let amount = Math.abs(value);
  let unit = 0;
  while (amount >= 1024 && unit < units.length - 1) {
    amount /= 1024;
    unit += 1;
  }
  const signed = value < 0 ? -amount : amount;
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: unit ? 1 : 0 }).format(signed)} ${units[unit]}`;
}

/** A measured number with the unit encoded by the API column name. */
export function formatNumber(key: string, value: number): string {
  if (/_percent$/.test(key))
    return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value)}%`;
  if (/_bytes$/.test(key)) return formatBytes(value);
  if (/_seconds$/.test(key))
    return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)} s`;
  if (/_ms$/.test(key))
    return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value)} ms`;
  return new Intl.NumberFormat().format(value);
}

function compactObject(value: Record<string, unknown>): string {
  const entries = Object.entries(value);
  const head = entries
    .slice(0, 3)
    .map(([key, item]) => `${humanize(key)}: ${typeof item === 'object' ? '…' : String(item)}`)
    .join(' · ');
  return `${head}${entries.length > 3 ? ` · +${entries.length - 3} more` : ''}`;
}

/** A single-line rendering of any column value, for a table cell. */
export function formatCell(key: string, value: unknown): string {
  if (isEmptyValue(value)) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return formatNumber(key, value);
  if (Array.isArray(value)) return value.map((item) => formatCell(key, item)).join(', ');
  if (typeof value === 'object') return compactObject(value as Record<string, unknown>);
  const text = String(value);
  if (isTimestampKey(key)) return formatTimestamp(text);
  const abbreviated = abbreviate(text);
  if (abbreviated !== text) return abbreviated;
  if (/^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/.test(text)) return humanize(text);
  return text;
}

/** The untruncated value, for the detail drawer and the copy button. */
export function formatFull(key: string, value: unknown): string {
  if (isEmptyValue(value)) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  const text = String(value);
  if (isTimestampKey(key)) return `${formatTimestamp(text)} (${formatRelative(text)})`;
  return text;
}

export function isMultiline(value: unknown): boolean {
  return typeof value === 'object' && value !== null;
}

export function recordKeys(records: readonly Record<string, unknown>[]): string[] {
  const seen = new Set<string>();
  for (const record of records) for (const key of Object.keys(record)) seen.add(key);
  return [...seen];
}

/**
 * Best-effort stable identity for a row, used as a React key and as the
 * drawer's selection. Control-plane tables disagree about what they call
 * their primary key, so try the usual suspects before falling back.
 */
export function recordIdentity(record: Record<string, unknown>, index: number): string {
  for (const key of ['id', 'request_id', 'droplet_id', 'provider_id', 'change_id', 'name']) {
    const value = record[key];
    if (typeof value === 'string' && value) return value;
  }
  return `row-${index}`;
}
